import React from 'react';
import { useTranslation } from 'next-i18next';
import { JocClubTier, TIER_CONFIGS, getTierProgress, formatJocAmount } from '~/constants/joc-club';
import { TierGemIcon } from './tier-gem-icon';

interface TierProgressProps {
  balanceWei: bigint | undefined;
  lockedWei?: bigint; // locked amount in wei
  showDetails?: boolean;
  className?: string;
}

export const TierProgress: React.FC<TierProgressProps> = ({
  balanceWei,
  lockedWei,
  showDetails = true,
  className,
}) => {
  const { t, i18n } = useTranslation('common');
  const { currentTier, nextTier, currentJoc, nextTierMinJoc, progress, remaining } =
    getTierProgress(balanceWei);

  const currentConfig = TIER_CONFIGS[currentTier];
  const nextConfig = nextTier ? TIER_CONFIGS[nextTier] : null;

  const currentTierName = i18n.language === 'ja' ? currentConfig.nameJa : currentConfig.name;
  const nextTierName = nextConfig
    ? i18n.language === 'ja'
      ? nextConfig.nameJa
      : nextConfig.name
    : null;

  // Calculate locked portion as percentage of the bar
  const lockedJoc = lockedWei ? Number(lockedWei / BigInt(10 ** 18)) : 0;
  const currentTierMin = TIER_CONFIGS[currentTier].minJoc;
  const range = nextTierMinJoc - currentTierMin;
  const lockedProgress =
    range > 0 ? Math.min(progress, (lockedJoc / range) * 100) : lockedJoc > 0 ? 100 : 0;

  // If not a member yet, show progress to Bronze
  if (currentTier === JocClubTier.NONE) {
    const bronzeConfig = TIER_CONFIGS[JocClubTier.BRONZE];
    const bronzeName = i18n.language === 'ja' ? bronzeConfig.nameJa : bronzeConfig.name;
    const progressToBronze = (currentJoc / bronzeConfig.minJoc) * 100;
    const lockedProgressToBronze =
      bronzeConfig.minJoc > 0 ? (lockedJoc / bronzeConfig.minJoc) * 100 : 0;

    return (
      <div className={className}>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-muted-foreground">{t('joc_club.join_club')}</span>
          <span className="font-medium">
            {formatJocAmount(currentJoc)} / {formatJocAmount(bronzeConfig.minJoc)} JOC
          </span>
        </div>
        <DualBar total={progressToBronze} locked={lockedProgressToBronze} />
        {showDetails && (
          <p className="text-xs text-muted-foreground mt-1">
            {t('joc_club.until_tier', {
              amount: formatJocAmount(bronzeConfig.minJoc - currentJoc),
              tier: bronzeName,
            })}
          </p>
        )}
      </div>
    );
  }

  // At Platinum - max tier
  if (!nextTier) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="flex items-center gap-1">
            <TierGemIcon tier={currentTier} className="h-4 w-4" />
            <span className={currentConfig.color}>{currentTierName}</span>
          </span>
          <span className="font-medium">{formatJocAmount(currentJoc)} JOC</span>
        </div>
        <DualBar total={100} locked={lockedJoc > 0 ? 100 : 0} />
        {showDetails && (
          <p className="text-xs text-muted-foreground mt-2 mb-1">
            {t('joc_club.max_tier_reached', { tier: currentTierName })}
          </p>
        )}
      </div>
    );
  }

  // Normal progress
  return (
    <div className={className}>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="flex items-center gap-1">
          <TierGemIcon tier={currentTier} className="h-4 w-4" />
          <span className={currentConfig.color}>{currentTierName}</span>
        </span>
        <span className="font-medium">
          {formatJocAmount(currentJoc)} / {formatJocAmount(nextTierMinJoc)} JOC
        </span>
      </div>
      <DualBar total={progress} locked={lockedProgress} />
      {showDetails && lockedJoc > 0 && (
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-yellow-400" />
            {t('joc_club.locked_joc')}: {formatJocAmount(lockedJoc)}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-primary" />
            {t('joc_club.wallet_balance')}: {formatJocAmount(currentJoc - lockedJoc)}
          </span>
        </div>
      )}
      {showDetails && (
        <p className="text-xs text-muted-foreground mt-1">
          {t('joc_club.until_tier', {
            amount: formatJocAmount(remaining),
            tier: nextTierName,
          })}
        </p>
      )}
    </div>
  );
};

/** Progress bar with locked (yellow) and total (primary) portions */
function DualBar({ total, locked }: { total: number; locked: number }) {
  return (
    <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
      {/* Total progress (primary color) */}
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-primary/30 transition-all"
        style={{ width: `${Math.min(100, Math.max(0, total))}%` }}
      />
      {/* Locked portion (yellow, on top) */}
      {locked > 0 && (
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-yellow-400 transition-all"
          style={{ width: `${Math.min(100, Math.max(0, locked))}%` }}
        />
      )}
    </div>
  );
}

export default TierProgress;
